import React, { KeyboardEvent } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";

import { MultipleSensorChartType } from "constant";
import { DEVICE_CHART_CONFIG, DeviceType } from "constant/deviceChart";

import deleteSvg from "assets/svg/delete.svg";

import { MultipleChartBlockProps, MultipleChartColumnData } from "types/Charts";
import { MultipleDeviceChartColumn } from "./MultipleDeviceChartColumn";
import Avatar from "@mui/material/Avatar";
import {
    getWidgetConfigByWidgetType,
    WidgetType,
} from "components/dashboard/utils/widgetConfig";

interface MultipleDeviceChartBlockProps extends MultipleChartBlockProps {
    deviceType: DeviceType;
}

// Generate default chart name based on device type and selected types
export const generateDefaultChartName = (
    deviceType: DeviceType,
    chartType: "same" | "different",
    type1?: string,
    type2?: string
): string => {
    const config = DEVICE_CHART_CONFIG[deviceType];

    if (chartType === "different" && type1 && type2) {
        // Two different types: "Compare Generic Digital Output with 4 Wire Motor Actuators"
        return `Compare ${type1} with ${type2} ${config.displayNamePlural}`;
    }

    if (type1) {
        // Same type: "Compare Relay Switch Actuators"
        return `Compare ${type1} ${config.displayNamePlural}`;
    }

    return config.defaultName;
};

export const MultipleDeviceChartBlock: React.FC<MultipleDeviceChartBlockProps> =
    React.memo((props) => {
        const {
            blockKey,
            onDelete,
            onChange,
            defaultValue,
            isSingleUpdate,
            isEdit,
            onSubmitMultipleChart,
            deviceType,
        } = props;

        const config = DEVICE_CHART_CONFIG[deviceType];

        const [formValues, setFormValues] =
            React.useState<MultipleChartColumnData>({
                multipleChartType: "same",
                chart_name: config.defaultName,
                data: {},
            });
        const [eventTrigger, setEventTrigger] = React.useState<any>();
        // Track if user has manually edited the chart name
        const [isNameManuallyEdited, setIsNameManuallyEdited] =
            React.useState<boolean>(false);

        React.useEffect(() => {
            if (defaultValue) {
                setFormValues(defaultValue);
                // If editing existing chart, consider the name as manually set
                if (isEdit) {
                    setIsNameManuallyEdited(true);
                }
            }
        }, [defaultValue, isEdit]);

        React.useEffect(() => {
            eventTrigger && onChange(formValues);
            setEventTrigger(undefined);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formValues]);

        // Get device IDs from the other column to exclude from selection
        const getExcludeDevices = (column: number): string[] => {
            if (formValues.multipleChartType !== "different") {
                return [];
            }
            const otherColumn = column === 0 ? 1 : 0;
            return Object.keys(formValues.data[otherColumn]?.devices || {});
        };

        const { minItems } = getWidgetConfigByWidgetType(
            WidgetType.ACTUATOR_CHART_MULTIPLE
        );
        const selectedActuatorCount = Object.values(formValues.data || {}).reduce(
            (count, column) => count + Object.keys(column?.devices ?? {}).length,
            0
        );
        const showMinActuatorsMessage =
            selectedActuatorCount < (minItems ?? 1);

        return (
            <div className="multiple-chart-block rounded p-4">
                <Row>
                    <Col xs={12}>
                        <Row>
                            <Col sm={12} className="mb-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div className="flex-grow-1">
                                        <p>Widget Name</p>
                                        <Form.Control
                                            type="text"
                                            className="multiple-chart-name"
                                            placeholder="Enter widget name"
                                            value={formValues.chart_name || ""}
                                            onChange={(event) => {
                                                setFormValues({
                                                    ...formValues,
                                                    chart_name:
                                                        event.target.value,
                                                });
                                                setIsNameManuallyEdited(true);
                                                setEventTrigger(event);
                                            }}
                                            onKeyPress={(
                                                e: KeyboardEvent<HTMLInputElement>
                                            ) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    if (onSubmitMultipleChart)
                                                        onSubmitMultipleChart(
                                                            e
                                                        );
                                                }
                                            }}
                                        />
                                    </div>
                                    {!isSingleUpdate && (
                                        <Avatar
                                            alt="delete"
                                            src={deleteSvg}
                                            onClick={() => onDelete?.(blockKey)}
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                cursor: "pointer",
                                            }}
                                        />
                                    )}
                                </div>
                            </Col>
                            <Col sm={12}>
                                <p>Select {config.displayNameLower} type</p>
                                <Row>
                                    {Object.keys(MultipleSensorChartType).map(
                                        (key) => (
                                            <Col sm={12} key={key} md={6}>
                                                <Form.Check
                                                    className={`multiple-chart-block__select-radio mb-1 mb-lg-0 ${formValues.multipleChartType ===
                                                        key &&
                                                        "multiple-chart-block__select-radio--active"
                                                        }`}
                                                    checked={
                                                        formValues.multipleChartType ===
                                                        key
                                                    }
                                                    type="radio"
                                                    label={
                                                        MultipleSensorChartType[
                                                        key as keyof typeof MultipleSensorChartType
                                                        ]
                                                    }
                                                    name={`multiple-${config.displayNameLower}-type-${blockKey.key}`}
                                                    id={`multiple-${config.displayNameLower}-type-${key}-${blockKey.key}`}
                                                    value={key}
                                                    custom
                                                    onChange={(event) => {
                                                        setFormValues(
                                                            (prev: any) => {
                                                                const newFormValues =
                                                                {
                                                                    ...prev,
                                                                    multipleChartType:
                                                                        key,
                                                                };

                                                                // Auto-update chart name when switching chart type
                                                                if (
                                                                    !isNameManuallyEdited
                                                                ) {
                                                                    const type1 =
                                                                        prev
                                                                            .data[0]
                                                                            ?.sensor_type;
                                                                    const type2 =
                                                                        prev
                                                                            .data[1]
                                                                            ?.sensor_type;

                                                                    newFormValues.chart_name =
                                                                        generateDefaultChartName(
                                                                            deviceType,
                                                                            key as
                                                                            | "same"
                                                                            | "different",
                                                                            type1,
                                                                            type2
                                                                        );
                                                                }

                                                                return newFormValues;
                                                            }
                                                        );
                                                        setEventTrigger(event);
                                                    }}
                                                />
                                            </Col>
                                        )
                                    )}
                                </Row>
                                <hr className="my-4 w-100" />
                            </Col>

                            <Col sm={12}>
                                <Row>
                                    {Array.from(
                                        new Array(
                                            formValues.multipleChartType ===
                                                "same"
                                                ? 1
                                                : 2 // Set number columns
                                        ).keys()
                                    ).map((column) => (
                                        <MultipleDeviceChartColumn
                                            key={
                                                formValues.multipleChartType +
                                                column
                                            }
                                            isEdit={isEdit}
                                            column={column}
                                            data={formValues}
                                            deviceType={deviceType}
                                            excludeDevices={getExcludeDevices(column)}
                                            onChange={(data) => {
                                                setFormValues((prev: any) => {
                                                    const newFormValues = {
                                                        ...prev,
                                                        data: {
                                                            ...prev.data,
                                                            [column]: data,
                                                        },
                                                    };

                                                    // Auto-update chart name if not manually edited
                                                    if (
                                                        !isNameManuallyEdited &&
                                                        data.sensor_type
                                                    ) {
                                                        const type1 =
                                                            column === 0
                                                                ? data.sensor_type
                                                                : prev.data[0]
                                                                    ?.sensor_type;
                                                        const type2 =
                                                            column === 1
                                                                ? data.sensor_type
                                                                : prev.data[1]
                                                                    ?.sensor_type;

                                                        newFormValues.chart_name =
                                                            generateDefaultChartName(
                                                                deviceType,
                                                                prev.multipleChartType,
                                                                type1,
                                                                type2
                                                            );
                                                    }

                                                    return newFormValues;
                                                });
                                                setEventTrigger("change");
                                            }}
                                        />
                                    ))}
                                </Row>
                            </Col>

                            {showMinActuatorsMessage && (
                                <Col sm={12} className="mt-3 text-danger">
                                    A minimum of two actuators must be selected.
                                </Col>
                            )}
                        </Row>
                    </Col>
                </Row>
            </div>
        );
    });
